import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { client_config } from "../../utils/clientConfigs";

import { log } from "../../utils/logger";
import { toastr } from "react-redux-toastr";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/free-regular-svg-icons";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Form,
  FormGroup,
  InputGroup,
  InputGroupAddon,
  Input,
  Label,
  Row,
  Col,
  FormFeedback,
  FormText,
  Progress,
} from "reactstrap";

const PasswordInput = ({ name, placeholder, value, show, setShowOption, onChange }) => (
  <InputGroup>
    <Input
      type={show ? "text" : "password"}
      name={name}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      autoComplete="new-password"
    />

    <InputGroupAddon addonType="append">
      <Button color="secondary" outline onClick={() => setShowOption(!show)} tabIndex={-1}>
        <FontAwesomeIcon icon={show ? faEyeSlash : faEye} />
      </Button>
    </InputGroupAddon>
  </InputGroup>
);

const PasswordInputs = ({
  user,
  disableCurrent,
  showNewPassword,
  setShowNewPassword,
  validatePasswords,
  onChange,
}) => (
  <Form onSubmit={validatePasswords}>
    <Row>
      <Col md={6}>
        <FormGroup>
          <Label for="email">Current Password</Label>
          <Input
            type="password"
            name="current_password"
            placeholder="Current Password"
            value={user.current_password}
            onChange={onChange}
            disabled={disableCurrent}
          />
        </FormGroup>
      </Col>
    </Row>

    <Row>
      <Col md={6}>
        <FormGroup>
          <Label for="new_password">New Password</Label>

          <PasswordInput
            name="new_password"
            value={user.new_password}
            placeholder="New Password"
            show={showNewPassword}
            setShowOption={setShowNewPassword}
            onChange={onChange}
          />
        </FormGroup>
      </Col>

      <Col>
        <FormGroup>
          <Label for="confirm_password">Confirm New Password</Label>

          <PasswordInput
            name="confirm_password"
            value={user.confirm_password}
            placeholder="Confirm New Password"
            show={showNewPassword}
            setShowOption={setShowNewPassword}
            onChange={onChange}
          />
        </FormGroup>
      </Col>
    </Row>

    <Row>
      <Col>
        <PasswordStrength user={user} />
      </Col>
    </Row>

    <Button type="submit" color="primary" onClick={validatePasswords}>
      Save changes
    </Button>
  </Form>
);

const testPasswordStrength = ({ user }) => {
  const { new_password, confirm_password } = user;
  const requirements = [];

  if (!new_password) {
    return { requirements };
  }

  if (new_password.length < 6) {
    return { error: "Password must be at least 6 characters", requirements };
  }
  if (confirm_password && new_password !== confirm_password) {
    return { error: "New passwords do not match", requirements };
  }
  if (new_password.toUpperCase() !== new_password) {
    requirements.push("lowercase");
  }
  if (new_password.toLowerCase() !== new_password) {
    requirements.push("uppercase");
  }
  if (/\d/.test(new_password)) {
    requirements.push("number");
  }
  if (/[^\w\d]/.test(new_password)) {
    requirements.push("special");
  }
  if (requirements.length < 2) {
    return {
      error:
        "Password must contain at least 2 of the following: lowercase, uppercase, number, special character",
      requirements: [],
    };
  }

  return { requirements };
};

const PasswordStrength = ({ user }) => {
  const { requirements, error } = testPasswordStrength({ user });
  const colors = ["danger", "warning", "primary", "success"];
  const messages = ["weak", "okay", "strong", "excellent!"];

  if (error) {
    return (
      <FormFeedback
        style={{ display: user.new_password ? "block" : "none" }}
        className={classNames({ "mb-4": user.new_password })}
      >
        {error}
      </FormFeedback>
    );
  }

  return (
    <FormText
      style={{ visibility: user.new_password ? "initial" : "hidden" }}
      className={classNames({ "mb-2": user.new_password })}
    >
      Password Strength
      <Progress color={colors[requirements.length - 1]} value={requirements.length} max={4}>
        {messages[requirements.length - 1]}
      </Progress>
    </FormText>
  );
};

const Security = ({ selected, user, disableCurrent, onChange, onSubmit }) => {
  const [showNewPassword, setShowNewPassword] = useState(false);

  if (selected !== "security") {
    return null;
  }

  const validatePasswords = (e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }

    const { current_password, new_password, confirm_password } = user;
    if (!current_password) {
      return toastr.error("Current Password", "Please enter your current password");
    }
    if (new_password === current_password) {
      return toastr.error(
        "New Password",
        "Your new password cannot be the same as your current one"
      );
    }
    if (!new_password) {
      return toastr.error("New Password", "Please enter a new password");
    }
    if (new_password !== confirm_password) {
      return toastr.error("New Password", "Your new passwords do not match, please double check");
    }
    const requirements = testPasswordStrength({ user });
    if (requirements.error) {
      return toastr.error("New Password", requirements.error);
    }

    log("Password is valid");
    onSubmit();
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle tag="h5" className="mb-0">
          Private info
        </CardTitle>
      </CardHeader>

      {disableCurrent && (
        <CardHeader>
          <CardTitle tag="h5" className="mb-0">
            The temporary password has been pre-filled for you
          </CardTitle>
        </CardHeader>
      )}

      <CardBody>
        <PasswordInputs
          user={user}
          showNewPassword={showNewPassword}
          setShowNewPassword={setShowNewPassword}
          validatePasswords={validatePasswords}
          onChange={onChange}
        />
      </CardBody>
    </Card>
  );
};

Security.propTypes = {
  selected: PropTypes.string,
  user: PropTypes.object.isRequired,
  disableCurrent: PropTypes.bool,

  // APIs
  onChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

export default Security;
